import { TOOL_LINKS } from "@/config/links";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { adminGridBtn } from "./locationToolsContentConstants";
import type { LocationToolsContentViewModel } from "./useLocationToolsContentModel";

export function LocationToolsLinkTypesSection({ vm }: { vm: LocationToolsContentViewModel }) {
  const {
    linkMetas,
    selectedLinkId,
    setSelectedLinkId,
    canEdit,
    loading,
    setShowAddTypeModal,
    selectedMeta,
    updateLinkTypeMeta,
    canDeleteLinkType,
    deleteLinkType,
    draft,
    updateBase,
    locOptions,
    setOverride,
  } = vm;

  return (
    <div className="space-y-4">
      <div
        {...{ [FIREFLY_TARGET_ATTR]: "lt-link-types-grid" }}
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3"
      >
        {linkMetas.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setSelectedLinkId(m.id === selectedLinkId ? null : m.id)}
            className={
              adminGridBtn +
              (selectedLinkId === m.id ? " ring-2 ring-indigo-400 border-indigo-300 dark:border-indigo-600" : "")
            }
          >
            <div className="font-semibold text-gray-900 dark:text-gray-100 text-sm line-clamp-2">{m.label}</div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">{m.description}</div>
          </button>
        ))}
        <button
          type="button"
          disabled={!canEdit || loading}
          onClick={() => setShowAddTypeModal(true)}
          className={
            adminGridBtn +
            " border-dashed border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 justify-center items-center text-center"
          }
        >
          <span className="text-2xl leading-none">+</span>
          <span className="text-sm font-semibold">Add link type</span>
        </button>
      </div>

      {selectedMeta ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-950/40 p-4 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="text-xs font-mono text-gray-500">{selectedMeta.id}</div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mt-2">Display name</label>
              <input
                className="mt-1 w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                value={selectedMeta.label}
                disabled={!canEdit}
                onChange={(e) => updateLinkTypeMeta(selectedMeta.id, e.target.value, selectedMeta.description)}
              />
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mt-2">Description</label>
              <input
                className="mt-1 w-full max-w-xl rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                value={selectedMeta.description}
                disabled={!canEdit}
                onChange={(e) => updateLinkTypeMeta(selectedMeta.id, selectedMeta.label, e.target.value)}
              />
            </div>
            {canDeleteLinkType && !TOOL_LINKS.some((t) => t.id === selectedMeta.id) ? (
              <button
                type="button"
                className="text-sm text-red-600 dark:text-red-400 hover:underline shrink-0"
                onClick={() => deleteLinkType(selectedMeta.id)}
              >
                Delete type (admin)
              </button>
            ) : null}
            {canDeleteLinkType && TOOL_LINKS.some((t) => t.id === selectedMeta.id) ? (
              <p className="text-xs text-gray-500 max-w-xs">
                Built-in types cannot be removed; you can clear URLs or hide usage by leaving defaults empty.
              </p>
            ) : null}
          </div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
            Org default URL
            <input
              type="url"
              disabled={!canEdit}
              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm disabled:opacity-60"
              value={draft.byId[selectedMeta.id]?.baseUrl ?? ""}
              onChange={(e) => updateBase(selectedMeta.id, e.target.value)}
              placeholder="https://…"
            />
          </label>
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-2">
            <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">Per-location overrides</div>
            {locOptions.length === 0 ? (
              <p className="text-xs text-gray-500">No locations yet. Use &quot;Add a new location&quot; above.</p>
            ) : (
              locOptions.map((loc) => (
                <div key={loc.id} className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
                  <span className="sm:w-48 shrink-0 text-gray-700 dark:text-gray-300 truncate">{loc.name}</span>
                  <input
                    type="url"
                    disabled={!canEdit}
                    className="flex-1 min-w-0 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1.5 text-xs font-mono disabled:opacity-60"
                    value={draft.byId[selectedMeta.id]?.perLocation?.[loc.id] ?? ""}
                    onChange={(e) => setOverride(selectedMeta.id, loc.id, e.target.value)}
                    placeholder="inherit org default"
                  />
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">Select a link type above, or add a new one.</p>
      )}
    </div>
  );
}
